import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import HearingRoundedIcon from "@mui/icons-material/HearingRounded";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import MicOffOutlinedIcon from "@mui/icons-material/MicOffOutlined";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../config/env";
import { LanguageContext } from "../context/LanguageContext";
import { useBrowserVoiceCommandSettings } from "../context/BrowserVoiceCommandContext";
import { useAuth } from "../hooks/AuthProvider";
import {
  BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT,
  fetchVoiceCommandConfig,
  normalizeVoiceCommandConfig,
  normalizeVoiceVideoEndBehavior,
  resolveTextVoiceCommand,
} from "../services/aiVoiceAssistantApi";
import "./BrowserVoiceCommands.css";

const TEXT = {
  permissionTitle: "Enable browser voice commands",
  permissionBody: "Allow microphone listening so Sama can react when you say the configured wake word.",
  enable: "Enable listening",
  notNow: "Not now",
  listening: "Listening",
  processing: "Processing request",
  unsupported: "Web Speech API is not available in this browser.",
  blocked: "Microphone access was blocked. Enable it from your browser permissions to use voice commands.",
  close: "Close",
};

const FAST_RESTART_THRESHOLD_MS = 1200;
const QUICK_RESTART_DELAY_MS = 450;
const SLOW_RESTART_DELAY_MS = 900;
const MAX_RESTART_DELAY_MS = 2400;

const WAKE_WORD_ALIASES = {
  sama: ["sama", "samaa", "samma", "samar", "summer", "soma", "samaah", "sam a", "some a", "سما", "ساما", "سمى"],
};

const EXTRA_WAKE_WORDS = [
  "sama",
  "samaa",
  "samma",
  "sam a",
  "some a",
  "summer",
  "soma",
  "sama ai",
  "sama iot",
  "sama system",
  "سما",
  "ساما",
  "سما اي او تي",
  "سما سيستم",
];

const WAKE_ACTIVATION_PREFIXES = [
  "hello",
  "hey",
  "hi",
  "helo",
  "hallo",
  "hey there",
  "hi there",
  "hello there",
  "مرحبا",
  "اهلا",
  "أهلا",
  "اهلين",
  "هلا",
  "هاي",
  "السلام عليكم",
];

const normalizeSpeechText = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u064B-\u065F\u0670]/g, "")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[أإآ]/g, "ا")
    .replace(/ى/g, "ي")
    .replace(/ة/g, "ه")
    .replace(/[،؟]/g, " ")
    .replace(/[^\p{L}\p{N}\s/_-]+/gu, " ")
    .replace(/\s+/g, " ");

const getExpandedWakeWords = (wakeWords = []) =>
  Array.from(
    new Set(
      [...wakeWords, ...EXTRA_WAKE_WORDS].flatMap((wakeWord) => {
        const normalizedWake = normalizeSpeechText(wakeWord);
        return [wakeWord, ...(WAKE_WORD_ALIASES[normalizedWake] || [])]
          .map(normalizeSpeechText)
          .filter(Boolean);
      })
    )
  );

const isBareWakeWord = (phrase, wakeWords = []) => {
  const normalizedPhrase = normalizeSpeechText(phrase);
  return Boolean(normalizedPhrase && getExpandedWakeWords(wakeWords).includes(normalizedPhrase));
};

const getWakeActivationPhrases = (wakeWords = []) => {
  const wakeAliases = getExpandedWakeWords(wakeWords);
  const configuredFullPhrases = (wakeWords || [])
    .map(normalizeSpeechText)
    .filter((wakeWord) => wakeWord && wakeWord.split(" ").length > 1 && !isBareWakeWord(wakeWord, []));

  return Array.from(
    new Set([
      ...configuredFullPhrases,
      ...WAKE_ACTIVATION_PREFIXES.flatMap((prefix) =>
        wakeAliases.map((wake) => normalizeSpeechText(`${prefix} ${wake}`))
      ),
    ].filter(Boolean))
  );
};

const containsAnyPhrase = (normalizedText, phrases = []) =>
  phrases.some((phrase) => {
    const normalizedPhrase = normalizeSpeechText(phrase);
    return Boolean(
      normalizedPhrase &&
        (normalizedText === normalizedPhrase ||
          normalizedText.startsWith(`${normalizedPhrase} `) ||
          normalizedText.endsWith(` ${normalizedPhrase}`) ||
          normalizedText.includes(` ${normalizedPhrase} `))
    );
  });

const getConfiguredBasicPhrases = (voiceConfig, subscriberKey = "") => {
  const subscriberActions = subscriberKey
    ? voiceConfig?.subscriberBasicActions?.[String(subscriberKey)] || []
    : [];
  const actions = subscriberActions.length ? subscriberActions : voiceConfig?.basicActions || [];
  const wakeWords = voiceConfig?.wakeWords || ["sama"];

  return Array.from(
    new Set(
      actions
        .flatMap((action) => (Array.isArray(action?.phrases) ? action.phrases : []))
        .flatMap((phrase) =>
          isBareWakeWord(phrase, wakeWords) ? getWakeActivationPhrases(wakeWords) : [phrase]
        )
        .map(normalizeSpeechText)
        .filter(Boolean)
    )
  );
};

const hasBasicActionPhrase = (transcript, voiceConfig, subscriberKey = "") =>
  containsAnyPhrase(normalizeSpeechText(transcript), getConfiguredBasicPhrases(voiceConfig, subscriberKey));

const hasGreeting = (transcript) =>
  containsAnyPhrase(normalizeSpeechText(transcript), WAKE_ACTIVATION_PREFIXES);

const hasWakeActivationPhrase = (transcript, wakeWords = []) =>
  containsAnyPhrase(normalizeSpeechText(transcript), getWakeActivationPhrases(wakeWords));

const getMediaBaseUrl = () => String(BASE_URL || "").replace(/\/+$/, "");

const mediaUrl = (mediaPath) => {
  if (!mediaPath) return "";
  if (/^(https?:|data:|blob:)/i.test(mediaPath)) return mediaPath;
  return `${getMediaBaseUrl()}/static/files/${String(mediaPath).replace(/^\/+/, "")}`;
};

const stripWakeWordFromTranscript = (transcript, wakeWords = []) => {
  const normalized = normalizeSpeechText(transcript);
  const expandedWakeWords = getWakeActivationPhrases(wakeWords).sort((a, b) => b.length - a.length);

  for (const wake of expandedWakeWords) {
    if (!wake) continue;
    if (normalized === wake) return "";
    if (normalized.startsWith(`${wake} `)) return normalized.slice(wake.length).trim();
    if (normalized.endsWith(` ${wake}`)) return "";
    if (normalized.includes(` ${wake} `)) return normalized.split(` ${wake} `).pop().trim();
  }

  return normalized;
};

export default function BrowserVoiceCommands() {
  const navigate = useNavigate();
  const { lang } = useContext(LanguageContext);
  const { org, permissions, user } = useAuth();
  const { settings, setEnabled } = useBrowserVoiceCommandSettings();
  const [config, setConfig] = useState(null);
  const [permissionPromptOpen, setPermissionPromptOpen] = useState(false);
  const [microphonePermission, setMicrophonePermission] = useState("prompt");
  const [overlay, setOverlay] = useState(null);
  const [listening, setListening] = useState(false);

  const recognitionRef = useRef(null);
  const shouldListenRef = useRef(false);
  const processingRef = useRef(false);
  const lastHandledRef = useRef({ text: "", at: 0 });
  const armedUntilRef = useRef(0);
  const greetingUntilRef = useRef(0);
  const pendingCommandRef = useRef("");
  const pendingCommandTimerRef = useRef(null);
  const speakResponseRef = useRef(() => {});
  const resolveAbortRef = useRef(null);
  const overlayTimerRef = useRef(null);
  const restartTimerRef = useRef(null);
  const restartDelayRef = useRef(SLOW_RESTART_DELAY_MS);
  const recognitionStartedAtRef = useRef(0);
  const lastResultAtRef = useRef(0);
  const organizationId = org?.id;
  const canUseVoiceCommand = Boolean(permissions?.ai_voice_command);
  const subscriberKey = String(user?.subid || org?.subid || "");

  const recognitionLanguage = useMemo(() => {
    const configuredLanguages = Array.isArray(config?.languages) ? config.languages : [];
    const preferredPrefix = lang === "ar" ? "ar" : "en";
    const preferred = configuredLanguages.find((item) =>
      String(item || "").toLowerCase().startsWith(preferredPrefix)
    );
    if (preferred) return preferred;
    if (configuredLanguages[0]) return configuredLanguages[0];
    return lang === "ar" ? "ar-SA" : "en-US";
  }, [config?.languages, lang]);

  const showOverlay = useCallback((nextOverlay, autoCloseMs = 4200) => {
    if (overlayTimerRef.current) {
      window.clearTimeout(overlayTimerRef.current);
      overlayTimerRef.current = null;
    }

    setOverlay(nextOverlay);

    if (autoCloseMs && nextOverlay?.type !== "media") {
      overlayTimerRef.current = window.setTimeout(() => {
        setOverlay(null);
        overlayTimerRef.current = null;
      }, autoCloseMs);
    }
  }, []);

  const applyAction = useCallback(
    (result) => {
      const action = result?.action;
      const responseText = result?.responseText || config?.unknownMessage || "";

      if (action?.type === "navigate" && action.path) {
        speakResponseRef.current(responseText);
        showOverlay({ type: "message", status: "ok", message: responseText }, 2200);
        navigate(action.path);
        return;
      }

      if (action?.type === "media" && action.media?.path) {
        const isVideoMedia = action.media?.mediaType === "video";
        if (!isVideoMedia) {
          speakResponseRef.current(responseText);
        }
        showOverlay({
          type: "media",
          status: "ok",
          message: isVideoMedia ? "" : responseText,
          media: {
            ...action.media,
            url: mediaUrl(action.media.path),
          },
        }, 0);
        return;
      }

      speakResponseRef.current(responseText);
      showOverlay({ type: "message", status: "ok", message: responseText });
    },
    [config?.unknownMessage, navigate, showOverlay]
  );

  const resolveTranscript = useCallback(
    async (transcript) => {
      if (!transcript || !organizationId) return;
      const normalizedTranscript = normalizeSpeechText(transcript);
      const now = Date.now();

      if (processingRef.current) return;
      if (
        lastHandledRef.current.text === normalizedTranscript &&
        now - lastHandledRef.current.at < 4500
      ) {
        return;
      }

      processingRef.current = true;
      lastHandledRef.current = { text: normalizedTranscript, at: now };

      resolveAbortRef.current?.abort();
      const controller = new AbortController();
      resolveAbortRef.current = controller;
      showOverlay({
        type: "message",
        status: "processing",
        message: TEXT.processing,
      }, 0);

      try {
        const result = await resolveTextVoiceCommand({
          transcript,
          language: lang,
          organizationId,
          signal: controller.signal,
        });
        const responseText = result?.responseText || config?.unknownMessage || "";
        applyAction({ ...result, transcript, responseText });
      } catch (error) {
        if (error?.code === "ERR_CANCELED" || error?.name === "CanceledError") return;
        const message = error?.response?.data?.message || config?.unknownMessage || TEXT.blocked;
        speakResponseRef.current(message);
        showOverlay({
          type: "message",
          status: "error",
          message,
        });
      } finally {
        processingRef.current = false;
        resolveAbortRef.current = null;
      }
    },
    [applyAction, config?.unknownMessage, lang, organizationId, showOverlay]
  );

  const scheduleCommandResolve = useCallback(
    (wakeWords) => {
      if (pendingCommandTimerRef.current) {
        window.clearTimeout(pendingCommandTimerRef.current);
      }

      pendingCommandTimerRef.current = window.setTimeout(() => {
        pendingCommandTimerRef.current = null;
        const command = normalizeSpeechText(pendingCommandRef.current);
        pendingCommandRef.current = "";
        armedUntilRef.current = 0;
        greetingUntilRef.current = 0;

        if (command) {
          resolveTranscript(`${wakeWords[0] || "sama"} ${command}`);
        }
      }, 1400);
    },
    [resolveTranscript]
  );

  const stopListening = useCallback(() => {
    shouldListenRef.current = false;
    restartDelayRef.current = SLOW_RESTART_DELAY_MS;
    recognitionStartedAtRef.current = 0;
    armedUntilRef.current = 0;
    greetingUntilRef.current = 0;
    pendingCommandRef.current = "";
    if (pendingCommandTimerRef.current) {
      window.clearTimeout(pendingCommandTimerRef.current);
      pendingCommandTimerRef.current = null;
    }
    if (restartTimerRef.current) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      try {
        recognitionRef.current.stop();
      } catch (_error) {}
      recognitionRef.current = null;
    }
    setListening(false);
  }, []);

  const startListening = useCallback((force = false) => {
    if (!config?.enabled || (!settings.enabled && !force) || !canUseVoiceCommand) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showOverlay({ type: "message", status: "error", message: TEXT.unsupported });
      return;
    }

    stopListening();
    shouldListenRef.current = true;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    recognition.lang = recognitionLanguage;

    recognition.onstart = () => {
      recognitionStartedAtRef.current = Date.now();
      setMicrophonePermission("granted");
      setListening(true);
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        shouldListenRef.current = false;
        setMicrophonePermission("denied");
        setListening(false);
        showOverlay({ type: "message", status: "error", message: TEXT.blocked });
        return;
      }

      if (event.error === "audio-capture") {
        shouldListenRef.current = false;
        setListening(false);
        showOverlay({ type: "message", status: "error", message: "No microphone was detected." });
        return;
      }

      setListening(false);
    };

    recognition.onresult = (event) => {
      if (processingRef.current) return;
      const result = event.results?.[event.results.length - 1];
      const transcript = Array.from(result || [])
        .map((item) => item?.transcript)
        .filter(Boolean)
        .join(" ")
        .trim();
      const wakeWords = config?.wakeWords || ["sama"];
      const heardGreeting = transcript && hasGreeting(transcript);
      const heardWakeActivationPhrase = transcript && hasWakeActivationPhrase(transcript, wakeWords);
      const hasWake = transcript && heardWakeActivationPhrase;
      const commandAfterWake = hasWake ? stripWakeWordFromTranscript(transcript, wakeWords) : "";
      const isBasicMode = config?.mode === "basic";
      const heardBasicPhrase = isBasicMode && hasBasicActionPhrase(transcript, config, subscriberKey);
      const armed = Date.now() <= armedUntilRef.current;

      if (!transcript) return;

      lastResultAtRef.current = Date.now();
      restartDelayRef.current = QUICK_RESTART_DELAY_MS;

      if (isBasicMode && (heardBasicPhrase || hasWake)) {
        greetingUntilRef.current = 0;
        armedUntilRef.current = 0;
        pendingCommandRef.current = "";
        if (pendingCommandTimerRef.current) {
          window.clearTimeout(pendingCommandTimerRef.current);
          pendingCommandTimerRef.current = null;
        }
        resolveTranscript(transcript);
        return;
      }

      if (heardGreeting && !heardWakeActivationPhrase) {
        greetingUntilRef.current = Date.now() + 6000;
        return;
      }

      if (hasWake && commandAfterWake) {
        armedUntilRef.current = Date.now() + 4500;
        greetingUntilRef.current = 0;
        pendingCommandRef.current = [pendingCommandRef.current, commandAfterWake]
          .map(normalizeSpeechText)
          .filter(Boolean)
          .join(" ");
        scheduleCommandResolve(wakeWords);
        return;
      }

      if (hasWake) {
        greetingUntilRef.current = 0;
        armedUntilRef.current = Date.now() + 7000;
        return;
      }

      if (armed) {
        armedUntilRef.current = Date.now() + 4500;
        pendingCommandRef.current = [pendingCommandRef.current, transcript]
          .map(normalizeSpeechText)
          .filter(Boolean)
          .join(" ");
        scheduleCommandResolve(wakeWords);
      }
    };

    recognition.onend = () => {
      setListening(false);
      if (recognitionRef.current === recognition) {
        recognitionRef.current = null;
      }
      if (!shouldListenRef.current) return;

      const ranForMs = Date.now() - recognitionStartedAtRef.current;
      const hadRecentResult = Date.now() - lastResultAtRef.current < 5000;
      const restartDelay = hadRecentResult
        ? QUICK_RESTART_DELAY_MS
        : ranForMs < FAST_RESTART_THRESHOLD_MS
          ? Math.min(Math.round(restartDelayRef.current * 1.35), MAX_RESTART_DELAY_MS)
          : SLOW_RESTART_DELAY_MS;

      restartDelayRef.current = restartDelay;
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        if (!shouldListenRef.current) return;
        startListening(true);
      }, restartDelay);
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
    } catch (error) {
      shouldListenRef.current = false;
      setListening(false);
      showOverlay({ type: "message", status: "error", message: error?.message || TEXT.unsupported });
    }
  }, [
    canUseVoiceCommand,
    config,
    recognitionLanguage,
    resolveTranscript,
    scheduleCommandResolve,
    settings.enabled,
    showOverlay,
    stopListening,
    subscriberKey,
  ]);

  useEffect(() => {
    speakResponseRef.current = (text) => {
      const message = String(text || "").trim();
      if (!message || !window.speechSynthesis) return;

      window.speechSynthesis.cancel();
      stopListening();

      const utterance = new SpeechSynthesisUtterance(message);
      utterance.lang = recognitionLanguage;
      utterance.rate = 0.95;
      utterance.pitch = 1;

      const resume = () => {
        window.setTimeout(() => {
          if (settings.enabled && config?.enabled && canUseVoiceCommand) {
            startListening();
          }
        }, 350);
      };

      utterance.onend = resume;
      utterance.onerror = resume;
      window.speechSynthesis.speak(utterance);
    };

    return () => {
      speakResponseRef.current = () => {};
      window.speechSynthesis?.cancel();
    };
  }, [canUseVoiceCommand, config?.enabled, recognitionLanguage, settings.enabled, startListening, stopListening]);

  useEffect(() => {
    let ignore = false;
    if (!canUseVoiceCommand) return undefined;

    fetchVoiceCommandConfig(organizationId)
      .then((nextConfig) => {
        if (!ignore) setConfig(nextConfig);
      })
      .catch((error) => {
        console.error("Failed to load browser voice command configuration:", error);
        if (!ignore) setConfig(null);
      });

    return () => {
      ignore = true;
    };
  }, [canUseVoiceCommand, organizationId]);

  useEffect(() => {
    const handleVoiceConfigUpdated = (event) => {
      const nextConfig = normalizeVoiceCommandConfig(event?.detail || {});
      lastHandledRef.current = { text: "", at: 0 };
      restartDelayRef.current = SLOW_RESTART_DELAY_MS;
      setConfig(nextConfig);
    };

    window.addEventListener(BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT, handleVoiceConfigUpdated);
    return () => {
      window.removeEventListener(BROWSER_VOICE_COMMAND_CONFIG_UPDATED_EVENT, handleVoiceConfigUpdated);
    };
  }, []);

  useEffect(() => {
    if (!window.navigator?.permissions?.query) return undefined;

    let permissionStatus;
    let ignore = false;

    window.navigator.permissions
      .query({ name: "microphone" })
      .then((status) => {
        if (ignore) return;
        permissionStatus = status;
        setMicrophonePermission(status.state || "prompt");
        status.onchange = () => setMicrophonePermission(status.state || "prompt");
      })
      .catch(() => {});

    return () => {
      ignore = true;
      if (permissionStatus) {
        permissionStatus.onchange = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!canUseVoiceCommand || !config?.enabled || !settings.enabled) {
      stopListening();
      return;
    }

    if (microphonePermission !== "granted") {
      setPermissionPromptOpen(true);
      return;
    }

    startListening();
  }, [
    canUseVoiceCommand,
    config?.enabled,
    microphonePermission,
    settings.enabled,
    startListening,
    stopListening,
  ]);

  useEffect(() => {
    return () => {
      resolveAbortRef.current?.abort();
      processingRef.current = false;
      stopListening();
      if (overlayTimerRef.current) {
        window.clearTimeout(overlayTimerRef.current);
      }
    };
  }, [stopListening]);

  const enableFromPrompt = () => {
    setPermissionPromptOpen(false);
    setEnabled(true);
    startListening(true);
  };

  const dismissPrompt = () => {
    setPermissionPromptOpen(false);
    setEnabled(false);
  };

  if (!canUseVoiceCommand || !config?.enabled) return null;

  return (
    <>
      {permissionPromptOpen ? (
        <div className="browser-vc-permission">
          <div className="browser-vc-permission-icon">
            <MicNoneOutlinedIcon />
          </div>
          <div>
            <strong>{TEXT.permissionTitle}</strong>
            <p>{TEXT.permissionBody}</p>
          </div>
          <div className="browser-vc-permission-actions">
            <button type="button" className="primary" onClick={enableFromPrompt}>
              {TEXT.enable}
            </button>
            <button type="button" onClick={dismissPrompt}>
              {TEXT.notNow}
            </button>
          </div>
        </div>
      ) : null}

      {overlay ? (
        <div className={`browser-vc-overlay is-${overlay.status || "idle"}${overlay.type === "media" ? " has-media" : ""}`}>
          <section className="browser-vc-card" aria-live="polite">
            <button
              type="button"
              className="browser-vc-close"
              onClick={() => setOverlay(null)}
              aria-label={TEXT.close}
            >
              <CloseRoundedIcon />
            </button>
            {overlay.type !== "media" ? (
              <>
                <div className="browser-vc-orb" aria-hidden="true">
                  {listening ? <HearingRoundedIcon /> : <MicOffOutlinedIcon />}
                </div>
                {overlay.message ? <strong>{overlay.message}</strong> : null}
              </>
            ) : null}
            {overlay.type === "media" ? (
              <div className="browser-vc-media">
                {overlay.media?.mediaType === "video" ? (
                  <video
                    src={overlay.media.url}
                    autoPlay
                    playsInline
                    controls
                    disablePictureInPicture
                    controlsList="nodownload nofullscreen noremoteplayback"
                    onContextMenu={(event) => event.preventDefault()}
                    onLoadedMetadata={(event) => {
                      event.currentTarget.muted = false;
                      event.currentTarget.play().catch(() => {});
                    }}
                    onEnded={(event) => {
                      const behavior = normalizeVoiceVideoEndBehavior(overlay.media?.videoEndBehavior);
                      if (behavior === "loop") {
                        event.currentTarget.currentTime = 0;
                        event.currentTarget.play().catch(() => {});
                        return;
                      }
                      if (behavior === "close") setOverlay(null);
                    }}
                  />
                ) : overlay.media?.mediaType === "image" ? (
                  <img src={overlay.media.url} alt={overlay.media.title || "Voice command media"} />
                ) : (
                  <iframe src={overlay.media?.url} title={overlay.media?.title || "Voice command file"} />
                )}
              </div>
            ) : null}
          </section>
        </div>
      ) : null}
    </>
  );
}
